import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling');
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const { refresh } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await apiClient.get(`/payments/status/${sessionId}`);
        setPaymentStatus(data.payment_status);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          await refresh();
          return;
        }
        if (data.status === 'expired' || attempts >= MAX_ATTEMPTS) {
          setStatus(data.status === 'expired' ? 'expired' : 'timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempts >= MAX_ATTEMPTS) { setStatus('error'); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-8" data-testid="success-page">
      <div className="max-w-lg w-full bg-white border border-slate-200 rounded-2xl p-10 shadow-xl text-center">
        {status === 'polling' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-slate-400 animate-spin mb-4" />
            <h1 className="font-heading text-2xl font-black mb-2">{t('confirmingPayment')}</h1>
            <p className="text-sm text-slate-500">{t('hangTight')}</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
            <h1 className="font-heading text-3xl font-black mb-2">{t('planActivated')}</h1>
            <p className="text-slate-600 mb-6">{t('planActiveCopy')}</p>
            <Link to="/create"><Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="success-cta">{t('startCreatingInvoices')}</Button></Link>
          </>
        )}
        {(status === 'timeout' || status === 'expired' || status === 'error') && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h1 className="font-heading text-2xl font-black mb-2">
              {status === 'expired' ? t('sessionExpired') : t('paymentNotConfirmed')}
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              {status === 'timeout' ? t('paymentTimeoutCopy') : t('paymentRetryCopy')}
            </p>
            <Link to="/plan"><Button variant="outline" data-testid="success-retry-btn">{t('backToPlan')}</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
